import React, { createContext, useContext } from "react";
import { AtGamesLinksCard, SternLinksCard } from "../../components/LinksCard";

export function getTournamentAppConfiguration(): TournamentConfiguration {
    const currentHost = window.location.hostname;

    // Stern
    if (currentHost.startsWith("flipals")) {
        const urlParams = new URLSearchParams(window.location.search);
        const rules = urlParams.get('rules');

        if (rules === 'birdsmouth') {
            return new BirdsmouthConfiguration();
        }
        return new SternTournamentAppConfiguration();
    }
    if (currentHost.startsWith("iflipal")) {
        return new IScoredTournamentAppConfiguration();
    }
    // Under The Glass
    if (currentHost.startsWith("underflipal")) {
        return new UTGTournamentAppConfiguration();
    }
    // AT Games
    if (currentHost.startsWith("flipal")) {
        return new AtGamesTournamentAppConfiguration();
    }

    throw new Error("Unknown path for tournament app configuration, check the domain!");
}

export interface EndPoints {
    results: string;
    tournamentsList: string;
    history: string;
}

export interface TournamentConfiguration {
    morePointsIsBetter: boolean;
    showInitials: boolean;
    showLogin: boolean;
    showFriends: boolean;
    showConsolidatedResults: boolean;
    endPoints: EndPoints;
    copyrightMessage: string;
    linksCard: (tournamentId: string) => React.ReactElement;
    missingGamePenalty: number;
    showTypeIndicator: boolean;
    cutoffRank: number;
    showCollage: boolean;
    showCalendar: boolean;
    showTournamentIdSelector: boolean;
    showPointPercentage: boolean;
    showIdIncrement: boolean;
    dropNLowest: number;
    showOverallRankInImagecollage: boolean;
    showScoresInImagecollage: boolean;
    qrScan: { processor: (data: string) => void } | null;
}

export class AtGamesTournamentAppConfiguration implements TournamentConfiguration {
    dropNLowest: number = 0;
    morePointsIsBetter = true;
    copyrightMessage = "Not affiliated with AT Games™, Legends™, or Flipper Friends™";
    showInitials: boolean = true;
    showLogin: boolean = true;
    showFriends: boolean = true;
    showConsolidatedResults: boolean = true;
    missingGamePenalty = 0;
    showTypeIndicator = true;
    cutoffRank = 50;
    showCollage = true;
    showCalendar = true;
    showTournamentIdSelector = true;
    showPointPercentage = true;
    showIdIncrement = true;
    showOverallRankInImagecollage = true;
    showScoresInImagecollage = true;
    qrScan = null;

    endPoints: EndPoints = {
        results: '/at/results/',
        tournamentsList: '/at/tournaments',
        history: '/at/history/'
    }

    linksCard = (tournamentId: string) => <AtGamesLinksCard tournamentId={tournamentId} />;
}


export class IScoredTournamentAppConfiguration implements TournamentConfiguration {
    dropNLowest: number = 0;
    morePointsIsBetter = false;
    copyrightMessage = "Not affiliated with iScored™";
    showInitials: boolean = false;
    showLogin: boolean = false;
    showFriends: boolean = false;
    showConsolidatedResults: boolean = false;
    missingGamePenalty = 1000;
    endPoints: EndPoints = {
        results: '/is/results/',
        tournamentsList: '/is/tournaments',
        history: '/is/history/'
    }
    showTypeIndicator = false;
    cutoffRank = 50;
    showCollage = true;
    showCalendar = false;
    showTournamentIdSelector = true;
    showPointPercentage = false;
    showIdIncrement = false;
    showOverallRankInImagecollage = false;
    showScoresInImagecollage = true;
    qrScan = null;
    // TODO: fix links card
    linksCard = (tournamentId: string) => <SternLinksCard tournamentId={tournamentId} />;
}

export class SternTournamentAppConfiguration implements TournamentConfiguration {
    dropNLowest: number = 0;
    morePointsIsBetter = false;
    copyrightMessage = "Not affiliated with Stern™ or Insider Connected™";
    showInitials: boolean = false;
    showLogin: boolean = false;
    showFriends: boolean = false;
    showConsolidatedResults: boolean = false;
    missingGamePenalty = 1000;
    endPoints: EndPoints = {
        results: '/st/results/',
        tournamentsList: '/st/tournaments',
        history: '/st/history/'
    }
    showTypeIndicator = true;
    cutoffRank = 4;
    showCollage = true;
    showCalendar = false;
    showTournamentIdSelector = true;
    showPointPercentage = false;
    showIdIncrement = false;
    showOverallRankInImagecollage = false;
    showScoresInImagecollage = true;
    qrScan = {
        processor: (data: string) => {
            const url = new URL(data);
            const pathParts = url.pathname.split('/').filter(part => part);
            const tournamentId = pathParts[pathParts.length - 1];
            const currentParams = new URLSearchParams(window.location.search);
            currentParams.set('tournamentId', tournamentId);
            window.location.href = `${window.location.origin}${window.location.pathname}?${currentParams.toString()}`;
            // window.location.reload();
        }
    };

    linksCard = (tournamentId: string) => <SternLinksCard tournamentId={tournamentId} />;
}

export class BirdsmouthConfiguration extends SternTournamentAppConfiguration {
    dropNLowest = 1;
    cutoffRank = 8;
}

export class UTGTournamentAppConfiguration implements TournamentConfiguration {
    dropNLowest: number = 0;
    morePointsIsBetter = false;
    copyrightMessage = "Not affiliated with Under The Glass™";
    showInitials: boolean = false;
    showLogin: boolean = false;
    showFriends: boolean = false;
    showConsolidatedResults: boolean = false;
    missingGamePenalty = 1000;
    endPoints: EndPoints = {
        results: '/utg/results/',
        tournamentsList: '/utg/tournaments',
        history: '/utg/history/'
    }
    showTypeIndicator = false;
    cutoffRank = 50;
    showCollage = true;
    showCalendar = false;
    showTournamentIdSelector = true;
    showPointPercentage = false;
    showIdIncrement = false;
    showOverallRankInImagecollage = false;
    showScoresInImagecollage = true;
    qrScan = null;
    linksCard = (tournamentId: string) => <SternLinksCard tournamentId={tournamentId} />; // Using SternLinksCard as a generic fallback since it doesn't do much specific
}

export const TournamentAppConfigurationContext = createContext<TournamentConfiguration | null>(null);

export const TournamentAppConfigurationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const helper = getTournamentAppConfiguration(); // Run once at app init
    return (
        <TournamentAppConfigurationContext.Provider value={helper}>
            {children}
        </TournamentAppConfigurationContext.Provider>
    );
};

export const useTournamentAppConfiguration = () => {
    const context = useContext(TournamentAppConfigurationContext);
    if (!context) throw new Error("useHelper must be used within a TournamentAppConfigurationProvider");
    return context;
};
